import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';
import { FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS } from '../../../utils/facilityReservationDate';

interface Facility {
  id: string;
  name: string;
  available: boolean;
  price: number;
  basePrice?: number; // 기본 이용가격 (시작종료형 전용)
  maxPrice?: number;
  notice: string;
  // 운영시간
  operatingHours: {
    start: number; // 0-23
    end: number; // 0-23
  };
  // 운영요일 (0: 일요일, 1: 월요일, ..., 6: 토요일)
  operatingDays: number[];
  // 휴무기간 목록
  closedPeriods?: Array<{
    id: string; // 고유 식별자
    start: {
      date: string; // YYYY-MM-DD
      time: number; // 0-23
    };
    end: {
      date: string; // YYYY-MM-DD
      time: number; // 0-23
    };
  }>;
  // 대표이미지 URL
  imageUrl?: string;
  reservationDays: number; // 예약 시작 가능일
  reservationWindowDays: number; // 예약 가능 기간
  reservationTimeUnit: number; // 예약시간단위
  reservationType: 'start-end' | 'time-slot'; // 예약타입
  // 회원 등급별 할인율
  discounts?: Array<{
    id: string; // 고유 식별자
    memberType: string | number; // 회원 등급
    discountRate: number; // 할인율 (%)
  }>;
}

type TabType = 'basic' | 'operation' | 'closed' | 'discount';

const FacilityDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, userLevel, permissions, loading: authLoading } = useAdminAuth();
  const [facility, setFacility] = useState<Facility | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [formData, setFormData] = useState<Facility>({
    id: '',
    name: '',
    available: true,
    price: 0,
    basePrice: undefined,
    maxPrice: undefined,
    notice: '',
    operatingHours: {
      start: 9,
      end: 18
    },
    operatingDays: [1, 2, 3, 4, 5], // 기본값: 월-금
    closedPeriods: [],
    imageUrl: '',
    reservationDays: 0,
    reservationWindowDays: FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS,
    reservationTimeUnit: 1,
    reservationType: 'start-end',
    discounts: []
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [newClosedPeriod, setNewClosedPeriod] = useState<{
    start: { date: string; time: number };
    end: { date: string; time: number };
  }>({
    start: { date: '', time: 0 },
    end: { date: '', time: 0 }
  });
  const [newDiscount, setNewDiscount] = useState<{
    memberType: string;
    discountRate: number;
  }>({
    memberType: '',
    discountRate: 0
  });

  // 회원 등급 목록 정의
  const memberLevels = [
    { value: 1, label: '최고 관리자', canDiscount: false },
    { value: 2, label: '일반 관리자', canDiscount: false },
    { value: 3, label: '공사 관계자', canDiscount: true },
    { value: 4, label: '센터 관계자', canDiscount: true },
    { value: 5, label: '입주업체', canDiscount: true },
    { value: 6, label: '입주협력업체', canDiscount: true },
    { value: 7, label: '공공기관', canDiscount: true },
    { value: 8, label: '항만근로자', canDiscount: true },
    { value: 9, label: '일반 사용자', canDiscount: true }
  ];

  // 관리자 인증 및 권한 체크
  useEffect(() => {
    if (!authLoading) {
      // 최고관리자이거나 시설물 관리 권한이 있는 경우에만 접근 가능
      if (!user || (userLevel !== 1 && !permissions?.facilities)) {
        navigate('/admin/dashboard');
        return;
      }
    }
  }, [user, userLevel, permissions, authLoading, navigate]);

  useEffect(() => {
    if (id && !authLoading && user) {
      fetchFacility();
    }
  }, [id, authLoading, user]);

  // URL 파라미터에서 edit=true가 있으면 자동으로 편집 모드로 진입
  useEffect(() => {
    const editParam = searchParams.get('edit');
    if (editParam === 'true') {
      setIsEditing(true);
      // URL에서 edit 파라미터 제거 (깔끔한 URL 유지)
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchFacility = async () => {
    try {
      if (!id) return;
      const docRef = doc(db, 'facilities', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = { id: docSnap.id, ...docSnap.data() } as Facility;
        setFacility(data);
        setFormData({
          id: data.id,
          name: data.name,
          available: data.available,
          price: data.price,
          basePrice: data.basePrice,
          maxPrice: data.maxPrice,
          notice: data.notice,
          operatingHours: data.operatingHours,
          operatingDays: data.operatingDays,
          closedPeriods: data.closedPeriods || [],
          imageUrl: data.imageUrl || '',
          reservationDays: data.reservationDays ?? 0,
          reservationWindowDays: data.reservationWindowDays ?? FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS,
          reservationTimeUnit: data.reservationTimeUnit ?? 1,
          reservationType: data.reservationType ?? 'start-end',
          discounts: data.discounts || []
        });
      }
    } catch (error) {
      console.error('시설물 정보 조회 중 오류 발생:', error);
    }
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      if (!id || !facility) return '';
      
      // 기존 이미지가 있다면 삭제
      if (facility.imageUrl) {
        try {
          // 다운로드 URL에서 파일 경로 추출
          const urlPath = decodeURIComponent(facility.imageUrl.split('/o/')[1].split('?')[0]);
          const oldImageRef = ref(storage, urlPath);
          await deleteObject(oldImageRef);
        } catch (deleteError) {
          console.error('기존 이미지 삭제 실패:', deleteError);
          // 이미지 삭제 실패해도 새 이미지 업로드는 계속 진행
        }
      }
      
      // 새 이미지 업로드
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const imageRef = ref(storage, `facilities/${fileName}`);
      const metadata = {
        contentType: file.type,
        createdAt: new Date().toISOString()
      };
      
      await uploadBytes(imageRef, file, metadata);
      const url = await getDownloadURL(imageRef);
      
      // UI 업데이트용
      setFormData(prev => ({
        ...prev,
        imageUrl: url
      }));
      
      return url;
    } catch (error) {
      console.error('이미지 업로드 중 오류 발생:', error);
      return '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!id) return;

      if (formData.reservationDays < 0) {
        alert('예약 시작 가능일은 0일 이상이어야 합니다.');
        return;
      }

      if (formData.reservationWindowDays < 0) {
        alert('예약 가능 기간은 0일 이상이어야 합니다.');
        return;
      }

      if (formData.reservationWindowDays < formData.reservationDays) {
        alert('예약 가능 기간은 예약 시작 가능일보다 크거나 같아야 합니다.');
        return;
      }
      
      let uploadedImageUrl = formData.imageUrl;
      
      // 이미지 업로드가 있다면 처리
      if (selectedImage) {
        uploadedImageUrl = await handleImageUpload(selectedImage);
        if (!uploadedImageUrl) {
          alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
          return;
        }
      }
      
      // 업데이트할 데이터 객체 생성
      const updateData: { [key: string]: any } = {
        name: formData.name,
        price: formData.price,
        notice: formData.notice,
        operatingHours: formData.operatingHours,
        operatingDays: formData.operatingDays,
        imageUrl: uploadedImageUrl,
        reservationType: formData.reservationType,
        reservationDays: formData.reservationDays,
        reservationWindowDays: formData.reservationWindowDays,
        reservationTimeUnit: formData.reservationTimeUnit,
        updatedAt: new Date()
      };
      
      // basePrice 처리 (undefined일 경우 null 사용)
      if (formData.basePrice === undefined || formData.basePrice === null || String(formData.basePrice) === '') {
        updateData.basePrice = null;
      } else {
        updateData.basePrice = Number(formData.basePrice);
      }
      
      // maxPrice 처리 (undefined일 경우 null 사용)
      if (formData.maxPrice === undefined || formData.maxPrice === null || String(formData.maxPrice) === '') {
        updateData.maxPrice = null;
      } else {
        updateData.maxPrice = Number(formData.maxPrice);
      }
      
      // Firestore 문서 업데이트
      await updateDoc(doc(db, 'facilities', id), updateData);
      
      
      await fetchFacility();
      setIsEditing(false);
    } catch (error) {
      console.error('시설물 정보 수정 중 오류 발생:', error);
      alert('시설물 정보 수정 중 오류가 발생했습니다: ' + error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'operatingHours.start' || name === 'operatingHours.end') {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        operatingHours: {
          ...prev.operatingHours,
          [child]: Number(value)
        }
      }));
    } else if (name === 'operatingDays') {
      const day = Number(value);
      setFormData(prev => ({
        ...prev,
        operatingDays: prev.operatingDays.includes(day)
          ? prev.operatingDays.filter(d => d !== day)
          : [...prev.operatingDays, day].sort()
      }));
    } else if (name.startsWith('discounts.')) {
      // 할인 정보 변경 처리
      const [_, discountId, field] = name.split('.');
      
      // 레벨 1, 2는 할인율을 설정할 수 없음
      if (field === 'memberType') {
        const selectedLevel = memberLevels.find(level => level.value.toString() === value);
        if (selectedLevel && !selectedLevel.canDiscount) {
          alert('관리자 등급(레벨 1, 2)은 할인율을 설정할 수 없습니다.');
          return;
        }
      }
      
      // 할인율 유효성 검사
      if (field === 'discountRate') {
        const discountRate = Number(value);
        if (isNaN(discountRate) || discountRate < 0 || discountRate > 100) {
          alert('할인율은 0에서 100 사이의 숫자만 입력 가능합니다.');
          return;
        }
      }
      
      setFormData(prev => ({
        ...prev,
        discounts: prev.discounts?.map(discount => 
          discount.id === discountId 
            ? { ...discount, [field]: field === 'discountRate' ? Number(value) : value } 
            : discount
        ) || []
      }));
    } else {
      const key = name as keyof Facility;
      setFormData(prev => ({
        ...prev,
        [key]: type === 'number' ? Number(value) : value
      }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleClosedPeriodChange = (type: 'start' | 'end', field: 'date' | 'time', value: string | number) => {
    setNewClosedPeriod(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: field === 'time' ? Number(value) : value
      }
    }));
  };

  const handleAddClosedPeriod = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!id) return;
      
      // 휴무기간 유효성 검사
      const startDate = new Date(newClosedPeriod.start.date);
      const endDate = new Date(newClosedPeriod.end.date);
      
      if (startDate > endDate) {
        alert('휴무 시작일이 종료일보다 늦을 수 없습니다.');
        return;
      }

      // 새로운 휴무기간 생성
      const newPeriod = {
        id: Date.now().toString(), // 임시 ID 생성
        start: newClosedPeriod.start,
        end: newClosedPeriod.end
      };

      // Firestore 문서 업데이트
      const updatedPeriods = [...(formData.closedPeriods || []), newPeriod];
      await updateDoc(doc(db, 'facilities', id), {
        closedPeriods: updatedPeriods
      });

      // 폼 데이터 업데이트
      setFormData(prev => ({
        ...prev,
        closedPeriods: updatedPeriods
      }));

      // 입력 폼 초기화
      setNewClosedPeriod({
        start: { date: '', time: 0 },
        end: { date: '', time: 0 }
      });
    } catch (error) {
      console.error('휴무기간 추가 중 오류 발생:', error);
    }
  };

  const handleDeleteClosedPeriod = async (periodId: string) => {
    try {
      if (!id) return;
      
      // Firestore 문서 업데이트
      const updatedPeriods = formData.closedPeriods?.filter(period => period.id !== periodId) || [];
      await updateDoc(doc(db, 'facilities', id), {
        closedPeriods: updatedPeriods
      });

      // 폼 데이터 업데이트
      setFormData(prev => ({
        ...prev,
        closedPeriods: updatedPeriods
      }));
    } catch (error) {
      console.error('휴무기간 삭제 중 오류 발생:', error);
    }
  };

  const handleAddDiscount = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!id) return;
      
      // 할인 정보 유효성 검사
      if (!newDiscount.memberType) {
        alert('회원 등급을 선택해주세요.');
        return;
      }
      
      // 할인율 유효성 검사
      const discountRate = Number(newDiscount.discountRate);
      if (isNaN(discountRate) || discountRate < 0 || discountRate > 100) {
        alert('할인율은 0에서 100 사이의 숫자만 입력 가능합니다.');
        return;
      }

      // 레벨 1, 2는 할인율을 설정할 수 없음
      const selectedLevel = memberLevels.find(level => level.value.toString() === newDiscount.memberType);
      if (selectedLevel && !selectedLevel.canDiscount) {
        alert('관리자 등급(레벨 1, 2)은 할인율을 설정할 수 없습니다.');
        return;
      }

      // 이미 존재하는 회원 등급인지 확인
      const existingDiscount = formData.discounts?.find(
        discount => String(discount.memberType) === newDiscount.memberType
      );
      
      if (existingDiscount) {
        alert('이미 해당 회원 등급에 대한 할인 정보가 존재합니다.');
        return;
      }

      // 새로운 할인 정보 생성
      const newDiscountItem = {
        id: Date.now().toString(), // 임시 ID 생성
        memberType: newDiscount.memberType,
        discountRate: discountRate
      };

      // Firestore 문서 업데이트
      const updatedDiscounts = [...(formData.discounts || []), newDiscountItem];
      await updateDoc(doc(db, 'facilities', id), {
        discounts: updatedDiscounts
      });

      // 폼 데이터 업데이트
      setFormData(prev => ({
        ...prev,
        discounts: updatedDiscounts
      }));

      // 입력 폼 초기화
      setNewDiscount({
        memberType: '',
        discountRate: 0
      });
    } catch (error) {
      console.error('할인 정보 추가 중 오류 발생:', error);
    }
  };

  const handleDeleteDiscount = async (discountId: string) => {
    try {
      if (!id) return;
      
      // Firestore 문서 업데이트
      const updatedDiscounts = formData.discounts?.filter(discount => discount.id !== discountId) || [];
      await updateDoc(doc(db, 'facilities', id), {
        discounts: updatedDiscounts
      });

      // 폼 데이터 업데이트
      setFormData(prev => ({
        ...prev,
        discounts: updatedDiscounts
      }));
    } catch (error) {
      console.error('할인 정보 삭제 중 오류 발생:', error);
    }
  };

  if (!facility) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  // 관리자 인증 로딩 중
  if (authLoading) {
    return (
      <div className="w-full min-h-screen bg-[#f7f9fd]">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#155acb]"></div>
        </div>
      </div>
    );
  }

  const weekdays = [
    { value: 0, label: '일' },
    { value: 1, label: '월' },
    { value: 2, label: '화' },
    { value: 3, label: '수' },
    { value: 4, label: '목' },
    { value: 5, label: '금' },
    { value: 6, label: '토' }
  ];

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="w-full min-h-screen bg-[#f7f9fd]">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="max-w-[1200px] mx-auto relative pt-8 px-4">
        {/* 헤더 섹션 */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-slate-800 text-4xl font-extrabold font-['Pretendard'] leading-9">
              시설물 상세 정보
            </div>
            <div className="space-x-2">
              <button
                onClick={() => navigate('/admin/facilities')}
                className="w-[100px] h-11 bg-[#6b7684] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#6b7684]/90 transition-colors"
              >
                목록으로
              </button>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="w-[80px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
              >
                {isEditing ? '취소' : '수정'}
              </button>
            </div>
          </div>
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>

        {/* 안내 메시지 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-6 bg-[#f2f6fb] border-l-4 border-[#155acb] rounded-r-sm">
            <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
              시설물의 상세 정보를 확인하고 수정할 수 있습니다.
            </p>
          </div>
        </div>

        {/* 탭 메뉴 */}
        <div className="mt-8 bg-white rounded-sm outline outline-1 outline-slate-200">
          <div className="p-8 pb-0">
            <div className="border-b border-[#e5e8eb]">
              <nav className="-mb-px flex space-x-8">
                {(['basic', 'operation', 'closed', 'discount'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`
                      py-4 px-1 border-b-2 font-medium text-sm font-['Pretendard']
                      ${activeTab === tab
                        ? 'border-[#155acb] text-[#155acb]'
                        : 'border-transparent text-[#6b7684] hover:text-[#1e2c49] hover:border-[#e5e8eb]'}
                    `}
                  >
                    {tab === 'basic' && '기본 정보'}
                    {tab === 'operation' && '운영 정보'}
                    {tab === 'closed' && '휴무 기간'}
                    {tab === 'discount' && '할인 정보'}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* 탭 컨텐츠 */}
          <div className="p-8">
            {activeTab === 'basic' && (
              <div className="space-y-6">
                {isEditing ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">시설물명</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">이용가격</label>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                        required
                      />
                    </div>

                    {formData.reservationType === 'start-end' && (
                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">기본 이용가격 (선택)</label>
                        <input
                          type="number"
                          name="basePrice"
                          value={formData.basePrice || ''}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          placeholder="첫 1시간 기본 요금을 입력하세요"
                        />
                        <p className="mt-2 text-sm text-[#6b7684] font-['Pretendard']">
                          기본 이용가격이 설정되면 첫 1시간은 기본 요금, 이후 시간은 시간당 이용가격이 적용됩니다.
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">최대 이용가격 (선택)</label>
                      <input
                        type="number"
                        name="maxPrice"
                        value={formData.maxPrice || ''}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                        placeholder="최대 이용가격을 입력하세요"
                      />
                    </div>

                    <div>
                      <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">기타안내</label>
                      <textarea
                        name="notice"
                        value={formData.notice}
                        onChange={handleChange}
                        rows={4}
                        className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                      />
                    </div>

                    <div>
                      <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">대표 이미지</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="mt-1 block w-full text-[#1e2c49] font-['Pretendard']"
                      />
                      {formData.imageUrl && (
                        <img
                          src={formData.imageUrl}
                          alt="현재 이미지"
                          className="mt-4 h-32 w-auto object-cover rounded-sm"
                        />
                      )}
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-[#1e2c49] text-lg font-bold font-['Pretendard']">예약 설정</h3>
                      
                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 시작 가능일</label>
                        <input
                          type="number"
                          name="reservationDays"
                          value={formData.reservationDays}
                          onChange={handleChange}
                          min="0"
                          className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          required
                        />
                        <p className="mt-2 text-sm text-[#6b7684] font-['Pretendard']">
                          0이면 당일부터, 3이면 신청일 기준 3일 후부터 예약할 수 있습니다.
                        </p>
                      </div>

                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약 가능 기간</label>
                        <input
                          type="number"
                          name="reservationWindowDays"
                          value={formData.reservationWindowDays}
                          onChange={handleChange}
                          min="0"
                          className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          required
                        />
                        <p className="mt-2 text-sm text-[#6b7684] font-['Pretendard']">
                          30이면 신청일 기준 30일 후 날짜까지만 예약할 수 있습니다.
                        </p>
                      </div>

                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약시간단위</label>
                        <input
                          type="number"
                          name="reservationTimeUnit"
                          value={formData.reservationTimeUnit}
                          onChange={handleChange}
                          min="1"
                          className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">예약타입</label>
                        <select
                          name="reservationType"
                          value={formData.reservationType}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          required
                        >
                          <option value="start-end">시작종료형</option>
                          <option value="time-slot">시간대형</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        type="submit"
                        className="w-[80px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                      >
                        저장
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">시설물명</h3>
                      <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">{facility.name}</p>
                    </div>

                    <div>
                      <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">이용상태</h3>
                      <p className="mt-1">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold font-['Pretendard'] ${
                            facility.available
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {facility.available ? '이용가능' : '점검중'}
                        </span>
                      </p>
                    </div>

                    <div>
                      <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">이용가격</h3>
                      <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">{facility.price.toLocaleString()}원</p>
                    </div>

                    {facility.basePrice && facility.reservationType === 'start-end' && (
                      <div>
                        <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">기본 이용가격</h3>
                        <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">{facility.basePrice.toLocaleString()}원</p>
                        <p className="mt-1 text-sm text-[#6b7684] font-['Pretendard']">(첫 1시간 기본 요금)</p>
                      </div>
                    )}

                    {facility.maxPrice && (
                      <div>
                        <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">최대 이용가격</h3>
                        <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">{facility.maxPrice.toLocaleString()}원</p>
                      </div>
                    )}

                    <div>
                      <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">기타안내</h3>
                      <p className="text-[#1e2c49] font-['Pretendard'] whitespace-pre-wrap">{facility.notice}</p>
                    </div>

                    {facility.imageUrl && (
                      <div>
                        <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">대표 이미지</h3>
                        <img
                          src={facility.imageUrl}
                          alt={facility.name}
                          className="mt-2 h-48 w-auto object-cover rounded-sm"
                        />
                      </div>
                    )}

                    <div>
                      <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">예약 설정</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-[#1e2c49] font-['Pretendard']">예약 시작 가능일: {facility.reservationDays}일 후</p>
                        <p className="text-[#1e2c49] font-['Pretendard']">예약 가능 기간: {facility.reservationWindowDays ?? FACILITY_RESERVATION_APPLICATION_WINDOW_DAYS}일</p>
                        <p className="text-[#1e2c49] font-['Pretendard']">예약시간단위: {facility.reservationTimeUnit}시간</p>
                        <p className="text-[#1e2c49] font-['Pretendard']">예약타입: {facility.reservationType === 'start-end' ? '시작종료형' : '시간대형'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'operation' && (
              <div className="space-y-6">
                {isEditing ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">운영시간</label>
                      <div className="flex items-center space-x-2 mt-1">
                        <select
                          name="operatingHours.start"
                          value={formData.operatingHours.start}
                          onChange={handleChange}
                          className="rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                        >
                          {hours.map(hour => (
                            <option key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}:00
                            </option>
                          ))}
                        </select>
                        <span className="text-[#1e2c49] font-['Pretendard']">~</span>
                        <select
                          name="operatingHours.end"
                          value={formData.operatingHours.end}
                          onChange={handleChange}
                          className="rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                        >
                          {hours.map(hour => (
                            <option key={hour} value={hour}>
                              {hour.toString().padStart(2, '0')}:00
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">운영요일</label>
                      <div className="mt-2 space-x-4">
                        {weekdays.map(day => (
                          <label key={day.value} className="inline-flex items-center">
                            <input
                              type="checkbox"
                              name="operatingDays"
                              value={day.value}
                              checked={formData.operatingDays.includes(day.value)}
                              onChange={handleChange}
                              className="rounded border-[#e5e8eb] text-[#155acb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb]"
                            />
                            <span className="ml-2 text-[#1e2c49] font-['Pretendard']">{day.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        type="submit"
                        className="w-[80px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                      >
                        저장
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">운영시간</h3>
                      <p className="text-[#1e2c49] text-lg font-medium font-['Pretendard']">
                        {facility.operatingHours.start.toString().padStart(2, '0')}:00 ~{' '}
                        {facility.operatingHours.end.toString().padStart(2, '0')}:00
                      </p>
                    </div>

                    <div>
                      <h3 className="text-[#6b7684] text-sm font-medium font-['Pretendard'] mb-2">운영요일</h3>
                      <p className="text-[#1e2c49] font-['Pretendard']">
                        {weekdays
                          .filter(day => facility.operatingDays.includes(day.value))
                          .map(day => day.label)
                          .join(', ')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'closed' && (
              <div className="space-y-6">
                {isEditing ? (
                  <>
                    {/* 기존 휴무기간 목록 */}
                    <div className="space-y-4">
                      <h3 className="text-[#1e2c49] text-lg font-bold font-['Pretendard']">휴무기간 목록</h3>
                      {formData.closedPeriods && formData.closedPeriods.length > 0 ? (
                        <div className="space-y-2">
                          {formData.closedPeriods.map(period => (
                            <div key={period.id} className="flex items-center justify-between bg-[#f8fbff] p-4 rounded-sm border border-[#e5e8eb]">
                              <div>
                                <p className="text-[#1e2c49] font-['Pretendard']">
                                  {period.start.date}{' '}
                                  {period.start.time.toString().padStart(2, '0')}:00 ~{' '}
                                  {period.end.date}{' '}
                                  {period.end.time.toString().padStart(2, '0')}:00
                                </p>
                              </div>
                              <button
                                onClick={() => handleDeleteClosedPeriod(period.id)}
                                className="text-[#6b7684] hover:text-red-500 focus:outline-none transition-colors"
                                title="휴무기간 삭제"
                              >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[#6b7684] font-['Pretendard']">설정된 휴무 기간이 없습니다.</p>
                      )}
                    </div>

                    {/* 새로운 휴무기간 추가 폼 */}
                    <form onSubmit={handleAddClosedPeriod} className="space-y-6 mt-8 p-6 bg-[#f8fbff] rounded-sm border border-[#e5e8eb]">
                      <h3 className="text-[#1e2c49] text-lg font-bold font-['Pretendard']">새 휴무기간 추가</h3>
                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">휴무 시작</label>
                        <div className="flex items-center space-x-2 mt-1">
                          <input
                            type="date"
                            value={newClosedPeriod.start.date}
                            onChange={(e) => handleClosedPeriodChange('start', 'date', e.target.value)}
                            className="rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          />
                          <select
                            value={newClosedPeriod.start.time}
                            onChange={(e) => handleClosedPeriodChange('start', 'time', e.target.value)}
                            className="rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          >
                            {hours.map(hour => (
                              <option key={hour} value={hour}>
                                {hour.toString().padStart(2, '0')}:00
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">휴무 종료</label>
                        <div className="flex items-center space-x-2 mt-1">
                          <input
                            type="date"
                            value={newClosedPeriod.end.date}
                            onChange={(e) => handleClosedPeriodChange('end', 'date', e.target.value)}
                            className="rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          />
                          <select
                            value={newClosedPeriod.end.time}
                            onChange={(e) => handleClosedPeriodChange('end', 'time', e.target.value)}
                            className="rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                          >
                            {hours.map(hour => (
                              <option key={hour} value={hour}>
                                {hour.toString().padStart(2, '0')}:00
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          className="w-[120px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                        >
                          휴무기간 추가
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="space-y-6">
                    {facility.closedPeriods && facility.closedPeriods.length > 0 ? (
                      <div className="space-y-2">
                        {facility.closedPeriods.map(period => (
                          <div key={period.id} className="bg-[#f8fbff] p-4 rounded-sm border border-[#e5e8eb]">
                            <p className="text-[#1e2c49] font-['Pretendard']">
                              {period.start.date}{' '}
                              {period.start.time.toString().padStart(2, '0')}:00 ~{' '}
                              {period.end.date}{' '}
                              {period.end.time.toString().padStart(2, '0')}:00
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#6b7684] font-['Pretendard']">설정된 휴무 기간이 없습니다.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'discount' && (
              <div className="space-y-6">
                {isEditing ? (
                  <>
                    {/* 기존 할인 정보 목록 */}
                    <div className="space-y-4">
                      <h3 className="text-[#1e2c49] text-lg font-bold font-['Pretendard']">회원 등급별 할인율 목록</h3>
                      {formData.discounts && formData.discounts.length > 0 ? (
                        <div className="space-y-2">
                          {formData.discounts.map(discount => (
                            <div key={discount.id} className="flex items-center justify-between bg-[#f8fbff] p-4 rounded-sm border border-[#e5e8eb]">
                              <div className="flex items-center space-x-4">
                                <div>
                                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">회원 등급</label>
                                  <select
                                    name={`discounts.${discount.id}.memberType`}
                                    value={discount.memberType}
                                    onChange={handleChange}
                                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                                  >
                                    {memberLevels.filter(level => level.canDiscount).map(level => (
                                      <option key={level.value} value={level.value.toString()}>
                                        {level.label} (레벨 {level.value})
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">할인율 (%)</label>
                                  <input
                                    type="text"
                                    name={`discounts.${discount.id}.discountRate`}
                                    value={discount.discountRate}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // 숫자만 입력 가능하도록 처리
                                      if (value === '' || /^\d+$/.test(value)) {
                                        const discountId = e.target.name.split('.')[1];
                                        const field = e.target.name.split('.')[2];
                                        setFormData(prev => ({
                                          ...prev,
                                          discounts: prev.discounts?.map(d => 
                                            d.id === discountId 
                                              ? { ...d, [field]: value === '' ? 0 : Number(value) } 
                                              : d
                                          ) || []
                                        }));
                                      }
                                    }}
                                    className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                                  />
                                </div>
                              </div>
                              <button
                                onClick={() => handleDeleteDiscount(discount.id)}
                                className="text-[#6b7684] hover:text-red-500 focus:outline-none transition-colors"
                                title="할인 정보 삭제"
                              >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[#6b7684] font-['Pretendard']">설정된 할인 정보가 없습니다.</p>
                      )}
                    </div>

                    {/* 새로운 할인 정보 추가 폼 */}
                    <form onSubmit={handleAddDiscount} className="space-y-6 mt-8 p-6 bg-[#f8fbff] rounded-sm border border-[#e5e8eb]">
                      <h3 className="text-[#1e2c49] text-lg font-bold font-['Pretendard']">새 할인 정보 추가</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">회원 등급</label>
                          <select
                            value={newDiscount.memberType}
                            onChange={(e) => setNewDiscount(prev => ({ ...prev, memberType: e.target.value }))}
                            className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                            required
                          >
                            <option value="">회원 등급 선택</option>
                            {memberLevels.filter(level => level.canDiscount).map(level => (
                              <option key={level.value} value={level.value.toString()}>
                                {level.label} (레벨 {level.value})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[#1e2c49] text-sm font-bold font-['Pretendard'] mb-2">할인율 (%)</label>
                          <input
                            type="text"
                            value={newDiscount.discountRate}
                            onChange={(e) => {
                              const value = e.target.value;
                              // 숫자만 입력 가능하도록 처리
                              if (value === '' || /^\d+$/.test(value)) {
                                setNewDiscount(prev => ({ ...prev, discountRate: value === '' ? 0 : Number(value) }));
                              }
                            }}
                            className="mt-1 block w-full rounded-sm border border-[#e5e8eb] shadow-sm focus:border-[#155acb] focus:ring-[#155acb] px-4 py-3 text-[#1e2c49] font-['Pretendard']"
                            placeholder="0-100 사이의 숫자"
                            required
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          className="w-[120px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
                        >
                          할인 정보 추가
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="space-y-6">
                    {facility.discounts && facility.discounts.length > 0 ? (
                      <div className="space-y-2">
                        {facility.discounts.map(discount => {
                          const memberLevel = memberLevels.find(
                            level => level.value.toString() === String(discount.memberType)
                          );
                          return (
                            <div key={discount.id} className="bg-[#f8fbff] p-4 rounded-sm border border-[#e5e8eb]">
                              <div className="flex justify-between items-center">
                                <p className="text-[#1e2c49] font-medium font-['Pretendard']">{memberLevel ? memberLevel.label : `레벨 ${discount.memberType}`}</p>
                                <p className="text-[#155acb] font-bold font-['Pretendard']">{discount.discountRate}% 할인</p>
                              </div>
                              <p className="text-sm text-[#6b7684] font-['Pretendard'] mt-1">
                                정상가격: {facility.price.toLocaleString()}원 → 
                                할인가격: {(facility.price * (1 - discount.discountRate / 100)).toLocaleString()}원
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[#6b7684] font-['Pretendard']">설정된 할인 정보가 없습니다.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16"></div>
      </div>
    </div>
  );
};

export default FacilityDetailPage; 
